import * as React from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { Alert, TableSortLabel } from "@mui/material";
import {
  typography,
  spacing,
  colors,
} from "../../../../../../../../../assets/styles/tokens";
import { useSelector } from "react-redux";
import { formatPercentage } from "../../../../../../../../../utils/helpers";
import dayjs from "dayjs";

interface Props {
  monthTableHeadings: { [key: string]: string };
}

const COLUMN_SORT_KEYS: Record<number, string> = {
  0: "brand_action_date",
  // 1: "perc_loaded_from_total",
  // 2: "Qftd_perc_loaded_from_total",
};

type Order = "asc" | "desc";

const ConversionsTableItem = ({ monthTableHeadings }: Props) => {
  const headCells = Object.entries(monthTableHeadings);
  const { conversionLastTwoWeeksQueryData } = useSelector(
    (state: any) => state.businessLeaderDashBoard,
  );
  const [orderBy, setOrderBy] = React.useState<string>("brand_action_date");
  const [order, setOrder] = React.useState<Order>("desc");

  const getTargetAlertConfig = (value: number) => {
    if (value <= 0.88) {
      return {
        severity: "error" as const,
        color: colors.Error[600].$value,
        icon: false,
      };
    }

    if (value >= 0.88 && value <= 0.93) {
      return {
        severity: "warning" as const,
        color: colors.Warning[600].$value,
        icon: false,
      };
    }

    return {
      severity: "success" as const,
      color: colors.Success[600].$value,
      icon: false,
    };
  };

  const handleSort = (columnKey: string) => {
    if (orderBy === columnKey) {
      setOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setOrderBy(columnKey);
      setOrder("desc");
    }
  };

  const rawRows: any[] = React.useMemo(
    () => conversionLastTwoWeeksQueryData?.query_output ?? [],
    [conversionLastTwoWeeksQueryData?.query_output],
  );

  const rows = React.useMemo(() => {
    return [...rawRows].sort((a, b) => {
      const aVal = a[orderBy];
      const bVal = b[orderBy];
      if (typeof aVal === "number" && typeof bVal === "number") {
        return order === "asc" ? aVal - bVal : bVal - aVal;
      }
      const aStr = String(aVal ?? "");
      const bStr = String(bVal ?? "");
      return order === "asc"
        ? aStr.localeCompare(bStr)
        : bStr.localeCompare(aStr);
    });
  }, [rawRows, orderBy, order]);

  return (
    <>
      <Table size="small">
        <TableHead sx={{ background: colors.GrayLightMode[100].$value }}>
          <TableRow
            sx={{
              // Hide all icons by default
              "& .MuiTableSortLabel-icon": {
                display: "none",
              },
              // Show icon only for first column
              "& th:first-of-type .MuiTableSortLabel-icon": {
                display: "inline-block",
              },
              "& th:first-of-type": {
                textAlign: "center",
              },
            }}
          >
            {headCells.map(([_, value], index) => {
              const sortKey = COLUMN_SORT_KEYS[index];
              const isActive = orderBy === sortKey;
              return (
                <TableCell
                  key={index}
                  align="left"
                  sx={{
                    whiteSpace: "nowrap",
                    fontSize: typography.Size.Text.xs.$value,
                    fontWeight: typography.Font_weight.Regular.$value,
                    paddingX: spacing.Spacing.xs.$value,
                    paddingY: spacing.Spacing.sm.$value,
                    lineHeight: typography.Line_height.text.xs.$value,
                    color: colors.GrayLightMode[900].$value
                  }}
                >
                  <TableSortLabel
                    active={isActive}
                    direction={isActive ? order : "desc"}
                    onClick={() => sortKey && handleSort(sortKey)}
                    sx={{
                      cursor: sortKey ? "pointer" : "default",
                    }}
                  >
                    {value && typeof value === "string" ? value : ""}
                  </TableSortLabel>
                </TableCell>
              );
            })}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows?.map((row: any, _i: number) => {
            return (
              <React.Fragment key={row?.brand_action_date}>
                <TableRow
                  sx={{
                    "& > *": {
                      borderBottom:
                        "1px solid " +
                        colors.GrayLightMode[200].$value +
                        "!important",
                    },
                    transition: "all 0.35s ease",
                    
                  }}
                >
                  <TableCell
                    align="left"
                    sx={{
                      whiteSpace: "nowrap",
                      fontSize: typography.Size.Text.xs.$value,
                      fontWeight: typography.Font_weight.Regular.$value,
                      paddingX: spacing.Spacing.xs.$value,
                      paddingY: spacing.Spacing.xs.$value,
                      color: colors.GrayLightMode[900].$value
                    }}
                  >
                    {dayjs(row?.brand_action_date)?.format("DD/MM/YYYY")}
                  </TableCell>
                  <TableCell
                    align="left"
                    sx={{
                      whiteSpace: "nowrap",
                      fontSize: typography.Size.Text.xs.$value,
                      fontWeight: typography.Font_weight.Regular.$value,
                      paddingX: spacing.Spacing.xs.$value,
                      paddingY: spacing.Spacing.xs.$value,
                      color: colors.GrayLightMode[900].$value
                    }}
                  >
                    <div
                      className="AlertItems"
                      style={{ display: "flex", alignItems: "center" }}
                    >
                      <span
                        style={{
                          fontWeight: typography.Font_weight.Semibold.$value,
                          fontSize: typography.Size.Text.xs.$value,
                          width:spacing.Spacing["7xl"].$value,
                        }}
                      >
                        {row?.FTD_loaded} / {row?.Total_ftd}
                      </span>
                      {(() => {
                        const config = getTargetAlertConfig(
                          row?.perc_loaded_from_total ?? 0,
                        );
                        return (
                          <Alert
                            icon={config.icon}
                            severity={config.severity}
                            sx={{
                              "& .MuiAlert-message": {
                                paddingY:
                                  spacing.Spacing.xxs.$value + " !important",
                                paddingX:
                                  spacing.Spacing.sm.$value + " !important",
                              },
                              color: config.color,
                              fontWeight: typography.Font_weight.Medium.$value,
                              paddingX: spacing.Spacing.none.$value,
                              paddingY: spacing.Spacing.none.$value,
                              lineHeight: typography.Line_height.text.xs.$value,
                              fontSize: typography.Size.Text.xs.$value,
                              display: "inline-flex",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {formatPercentage(row?.perc_loaded_from_total)}
                          </Alert>
                        );
                      })()}
                    </div>
                  </TableCell>
                  <TableCell
                    align="left"
                    sx={{
                      whiteSpace: "nowrap",
                      fontSize: typography.Size.Text.xs.$value,
                      fontWeight: typography.Font_weight.Regular.$value,
                      paddingX: spacing.Spacing.xs.$value,
                      paddingY: spacing.Spacing.xs.$value,
                      color: colors.GrayLightMode[900].$value
                    }}
                  >
                    <div
                      className="AlertItems"
                      style={{ display: "flex", alignItems: "center" }}
                    >
                      <span
                        style={{
                          fontWeight: typography.Font_weight.Semibold.$value,
                          fontSize: typography.Size.Text.xs.$value,
                          width:spacing.Spacing["7xl"].$value,
                        }}
                      >
                        {row?.QFTD_loaded} / {row?.Total_Qftd}
                      </span>
                      {(() => {
                        const config = getTargetAlertConfig(
                          row?.Qftd_perc_loaded_from_total ?? 0,
                        );
                        return (
                          <Alert
                            icon={config.icon}
                            severity={config.severity}
                            sx={{
                              "& .MuiAlert-message": {
                                paddingY:
                                  spacing.Spacing.xxs.$value + " !important",
                                paddingX:
                                  spacing.Spacing.sm.$value + " !important",
                              },
                              color: config.color,
                              fontWeight: typography.Font_weight.Medium.$value,
                              paddingX: spacing.Spacing.none.$value,
                              paddingY: spacing.Spacing.none.$value,
                              lineHeight: typography.Line_height.text.xs.$value,
                              fontSize: typography.Size.Text.xs.$value,
                              display: "inline-flex",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {formatPercentage(row?.Qftd_perc_loaded_from_total)}
                          </Alert>
                        );
                      })()}
                    </div>
                  </TableCell>
                </TableRow>
              </React.Fragment>
            );
          })}
        </TableBody>
      </Table>
    </>
  );
};

export default ConversionsTableItem;
